import { useCallback, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Activity, RefreshCw, Zap } from "lucide-react";
import type { AppAutocompleteItem } from "@a7y/types";
import { apiStoreSearchAutocomplete } from "@a7y/api-client";
import {
  AppSearchAutocomplete,
  type AppSearchQueryInfo,
} from "@a7y/ui";
import { cn } from "@/lib/utils";

interface QueryRun extends AppSearchQueryInfo {
  id: number;
  startedAt: number;
}

const RECENT_QUERIES_CAP = 25;

function SourceBadge({ source }: { source: AppSearchQueryInfo["source"] }) {
  const isHot = source === "algolia";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium",
        isHot
          ? "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
          : "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
      )}
    >
      {isHot ? <Zap className="h-3 w-3" /> : <RefreshCw className="h-3 w-3" />}
      {source === "algolia"
        ? "Algolia"
        : source === "mixed"
          ? "Mixed"
          : "Live"}
    </span>
  );
}

export function AppQuickSearchTab() {
  const { getToken } = useAuth();
  const [selected, setSelected] = useState<AppAutocompleteItem | null>(null);
  const [history, setHistory] = useState<QueryRun[]>([]);
  const queryIdRef = useRef(0);

  const searchLive = useCallback(
    async (term: string) => {
      const token = await getToken();
      const resp = await apiStoreSearchAutocomplete(token, term);
      if (!resp.success) {
        throw new Error(resp.error ?? "autocomplete returned success=false");
      }
      return resp.results ?? [];
    },
    [getToken],
  );

  const onQueryComplete = useCallback((info: AppSearchQueryInfo) => {
    queryIdRef.current += 1;
    setHistory((prev) =>
      [
        { id: queryIdRef.current, startedAt: Date.now(), ...info },
        ...prev,
      ].slice(0, RECENT_QUERIES_CAP),
    );
  }, []);

  const stats = useMemo(() => {
    if (history.length === 0) return null;
    const algolia = history.filter((h) => h.source === "algolia");
    const mixed = history.filter((h) => h.source === "mixed");
    const live = history.filter((h) => h.source === "live");
    const avg = (arr: QueryRun[]) =>
      arr.length === 0
        ? null
        : Math.round(arr.reduce((s, h) => s + h.durationMs, 0) / arr.length);
    return {
      total: history.length,
      algoliaHits: algolia.length,
      mixedHits: mixed.length,
      liveHits: live.length,
      algoliaAvgMs: avg(algolia),
      mixedAvgMs: avg(mixed),
      liveAvgMs: avg(live),
    };
  }, [history]);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-4">
        <AppSearchAutocomplete
          onSelect={setSelected}
          searchLive={searchLive}
          placeholder="Type an app name (e.g. Spotify, Notion, Duolingo)…"
          autoFocus
          testIdPrefix="admin-app-search"
          algoliaLogoLightSrc="/admin/img/brand/algolia-logo-blue.svg"
          algoliaLogoDarkSrc="/admin/img/brand/algolia-logo-white.svg"
          onQueryComplete={onQueryComplete}
        />

        {selected && (
          <div className="rounded-lg border border-zinc-200 bg-white p-4 text-sm dark:border-zinc-700 dark:bg-zinc-900">
            <div className="mb-2 font-semibold">Selected</div>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
              <dt className="text-zinc-500">Name</dt>
              <dd className="font-medium">{selected.name}</dd>
              <dt className="text-zinc-500">Store</dt>
              <dd>{selected.store}</dd>
              <dt className="text-zinc-500">Store ID</dt>
              <dd className="font-mono">{selected.storeId}</dd>
              {selected.developer && (
                <>
                  <dt className="text-zinc-500">Developer</dt>
                  <dd>{selected.developer}</dd>
                </>
              )}
              {selected.bundleId && (
                <>
                  <dt className="text-zinc-500">Bundle ID</dt>
                  <dd className="font-mono">{selected.bundleId}</dd>
                </>
              )}
            </dl>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Activity className="h-4 w-4" /> Session stats
          </h3>
          {!stats ? (
            <p className="text-xs text-zinc-500">No queries yet.</p>
          ) : (
            <dl className="grid grid-cols-2 gap-y-1 text-xs">
              <dt className="text-zinc-500">Total queries</dt>
              <dd className="text-right font-medium tabular-nums">
                {stats.total}
              </dd>
              <dt className="text-zinc-500">Algolia-only</dt>
              <dd className="text-right font-medium tabular-nums">
                {stats.algoliaHits}
              </dd>
              <dt className="text-zinc-500">Mixed (backfilled)</dt>
              <dd className="text-right font-medium tabular-nums">
                {stats.mixedHits}
              </dd>
              <dt className="text-zinc-500">Live-only</dt>
              <dd className="text-right font-medium tabular-nums">
                {stats.liveHits}
              </dd>
              <dt className="text-zinc-500">Avg Algolia ms</dt>
              <dd className="text-right font-medium tabular-nums">
                {stats.algoliaAvgMs ?? "—"}
              </dd>
              <dt className="text-zinc-500">Avg mixed ms</dt>
              <dd className="text-right font-medium tabular-nums">
                {stats.mixedAvgMs ?? "—"}
              </dd>
              <dt className="text-zinc-500">Avg live ms</dt>
              <dd className="text-right font-medium tabular-nums">
                {stats.liveAvgMs ?? "—"}
              </dd>
            </dl>
          )}
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
          <div className="border-b border-zinc-200 px-4 py-2 text-sm font-semibold dark:border-zinc-700">
            Recent queries
          </div>
          {history.length === 0 ? (
            <p className="px-4 py-3 text-xs text-zinc-500">No queries yet.</p>
          ) : (
            <ul className="max-h-[420px] divide-y divide-zinc-100 overflow-y-auto dark:divide-zinc-800">
              {history.map((h) => (
                <li
                  key={h.id}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs"
                >
                  <SourceBadge source={h.source} />
                  <span className="flex-1 truncate">{h.term}</span>
                  <span className="tabular-nums text-zinc-500">
                    {Math.round(h.durationMs)}ms
                  </span>
                  <span className="tabular-nums text-zinc-400">
                    {h.error ? "err" : `${h.resultCount}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
